import { ehSubGrupo, type NavItem, type NavSubItem } from './nav-config';

/**
 * Trilha (breadcrumb) e destaque do menu — lógica pura extraída do AppShell [AB#1095] para ser testável
 * (e para o AppShell exportar só componente).
 */

export interface TrilhaItem {
  label: string;
  path?: string;
}

/**
 * Subpáginas conhecidas que pendurar como 3º nível do breadcrumb quando a
 * rota não bate diretamente com um item do nav. Mapeia rota exata → label.
 * Caminhos com prefixo de algum item do nav herdam o pai como link
 * clicável, mantendo a navegação consistente.
 *
 * Quando adicionar uma nova subpágina filha, append aqui — é o único
 * ponto de extensão, sem necessidade de breadcrumb local em cada arquivo.
 */
const SUBPATHS_NAV: Array<{ pattern: RegExp; label: string }> = [
  {
    pattern: /^\/minha-conta\/assinatura\/pagamentos\/?$/,
    label: 'Histórico de pagamentos',
  },
];

/**
 * [Bug#830 — breadcrumb do Financeiro] Quão bem `navPath` casa com a URL atual: 0 = não casa,
 * 1 = só o caminho, 2 = caminho + query. Itens do menu podem trazer query
 * (`/financeiro/titulos?direcao=PAGAR`); comparar `pathname` com esse texto inteiro nunca casava
 * e a trilha caía em "Início". Com query no item, a query da URL precisa bater (Pagar × Receber
 * compartilham o mesmo caminho).
 */
export function pontuarNav(pathname: string, search: string, navPath: string | undefined): 0 | 1 | 2 {
  if (!navPath) return 0;
  const [caminho = '', consulta] = navPath.split('?');
  if (!isAtivo(pathname, caminho)) return 0;
  if (!consulta) return 2;
  const esperado = new URLSearchParams(consulta);
  const atual = new URLSearchParams(search);
  for (const [k, v] of esperado) if (atual.get(k) !== v) return 1;
  return 2;
}

/**
 * Destaque do item no menu lateral. Item COM query (`/financeiro/titulos?direcao=PAGAR`): só
 * ativo quando caminho + query batem — o `isActive` do NavLink compara só o caminho e acenderia
 * Pagar e Receber juntos. Item sem query: como sempre (`isActive` do NavLink ou caminho).
 */
export function itemAtivo(pathname: string, search: string, path: string | undefined, isActive = false): boolean {
  if (!path) return false;
  if (path.includes('?')) return pontuarNav(pathname, search, path) === 2;
  return isActive || pontuarNav(pathname, search, path) === 2;
}

export function trilhaPara(pathname: string, search: string, nav: NavItem[]): TrilhaItem[] {
  for (const item of nav) {
    if (item.path && pontuarNav(pathname, search, item.path) > 0) {
      const caminhoItem = item.path.split('?')[0];
      // Rota é folha direta do nav: ex. /minha-conta/assinatura.
      if (pathname === caminhoItem) {
        return [{ label: item.label, path: item.path }];
      }
      // Rota é uma subpágina filha (ex.: /minha-conta/assinatura/pagamentos)
      // — pai do nav vira link clicável, subpágina entra como 2º nível.
      const sub = SUBPATHS_NAV.find((s) => s.pattern.test(pathname));
      return sub
        ? [{ label: item.label, path: item.path }, { label: sub.label }]
        : [{ label: item.label, path: item.path }];
    }
    if (item.children) {
      // Melhor casamento entre os filhos (query casada vence caminho-só).
      // [AB#1095] Empate de nota: o caminho MAIS LONGO vence — `/contabilidade/dre/estrutura` casa
      // com `/contabilidade/dre` (prefixo) e com o próprio item; sem isto a trilha mostrava "DRE".
      let melhor: { filho: NavSubItem; grupo?: string; nota: 1 | 2; tam: number } | null = null;
      const candidatos = item.children.flatMap((f): Array<{ filho: NavSubItem; grupo?: string }> =>
        ehSubGrupo(f) ? f.itens.map((i) => ({ filho: i, grupo: f.label })) : [{ filho: f }],
      );
      for (const { filho, grupo } of candidatos) {
        const nota = pontuarNav(pathname, search, filho.path);
        const tam = filho.path.split('?')[0].length;
        if (nota > 0 && (melhor === null || nota > melhor.nota || (nota === melhor.nota && tam > melhor.tam))) {
          melhor = { filho, grupo, nota: nota as 1 | 2, tam };
        }
      }
      if (melhor) {
        const { filho, grupo } = melhor;
        const base: TrilhaItem[] = [
          { label: item.label },
          ...(grupo ? [{ label: grupo }] : []),
          { label: filho.label, path: filho.path },
        ];
        // Aba ativa (`?aba=`; sem ela, a primeira) como último nível — ex.: Financeiro › Contas a receber › Cobrança › Lotes.
        if (filho.abas?.length) {
          const pedida = new URLSearchParams(search).get('aba');
          const aba = filho.abas.find((a) => a.id === pedida) ?? filho.abas[0]!;
          base.push({ label: aba.label });
        }
        // Subpágina sob um item filho — ex.: Assinatura → Histórico de
        // pagamentos. Vira 3º nível, filho do nav passa a ser link.
        if (pathname !== filho.path.split('?')[0]) {
          const sub = SUBPATHS_NAV.find((s) => s.pattern.test(pathname));
          if (sub) base.push({ label: sub.label });
        }
        return base;
      }
    }
  }
  return [{ label: 'Início' }];
}

export function isAtivo(pathname: string, path: string | undefined): boolean {
  if (!path) return false;
  if (path === pathname) return true;
  // Considera "/admin/tenants" ativo em "/admin/tenants/123" etc.
  return pathname.startsWith(`${path}/`);
}

